/**
 * Tell the page what kind of scrollbar this machine draws.
 *
 * Windows and Linux draw classic scrollbars: they take up space, stay put, and carry the OS
 * stepper arrows. macOS draws overlay ones: no space, painted over the content, faded out
 * whenever you are not actively scrolling — which is what made a wide table look as though it
 * had no scroll affordance at all.
 *
 * Styling `::-webkit-scrollbar` fixes the overlay case, but it swaps the OS widget for a
 * custom one *everywhere*, which throws away the Windows arrows and stops matching every
 * unstyled scrollbar in the app (the sidebar, the chat). So measure once and let the CSS paint
 * its own bar only where the native one is invisible.
 *
 * Also publishes the measured thickness, since a container holding a native bar has to be big
 * enough for it or the arrows get clipped off, and the page body pays for it out of its own
 * right-hand gutter.
 */
export default function detectScrollbars(root = document.documentElement) {
  const probe = document.createElement('div');
  probe.style.cssText = 'position:absolute;top:-9999px;width:100px;height:100px;overflow:scroll';
  document.body.appendChild(probe);
  const nativeHeight = probe.offsetHeight - probe.clientHeight;
  probe.remove();

  const isOverlay = nativeHeight === 0;
  root.classList.toggle('has-overlay-scrollbars', isOverlay);
  // Overlay bars report 0; they still need a strip to be painted into, and 10px is what the
  // custom bar we paint there is. Same figure on both axes.
  root.style.setProperty('--native-scrollbar-size', `${Math.max(nativeHeight, 10)}px`);
  return { nativeHeight, isOverlay };
}
